import { useState } from 'react'
import { Card, Tag, Button, Space, Input, message, Popconfirm, Empty } from 'antd'
import { PlusOutlined, DeleteOutlined } from '@ant-design/icons'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { hospitalCapabilitiesApi } from '@/api/hospital-capabilities'
import { getApiErrorMessage } from '@/utils/error'

interface HospitalCapabilitiesProps {
  hospitalId: string
}

export const HospitalCapabilities = ({ hospitalId }: HospitalCapabilitiesProps) => {
  const [newCapability, setNewCapability] = useState('')
  const [isAdding, setIsAdding] = useState(false)
  const queryClient = useQueryClient()

  const { data, isLoading } = useQuery({
    queryKey: ['hospital-capabilities', hospitalId],
    queryFn: async () => {
      const response = await hospitalCapabilitiesApi.getCapabilities(hospitalId)
      return response.data.capabilities
    },
  })

  const addCapabilityMutation = useMutation({
    mutationFn: async (capability: string) => {
      return hospitalCapabilitiesApi.addCapability(hospitalId, { capability })
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hospital-capabilities', hospitalId] })
      message.success('Capability added successfully')
      setNewCapability('')
      setIsAdding(false)
    },
    onError: (error: unknown) => {
      message.error(getApiErrorMessage(error, 'Failed to add capability'))
    },
  })

  const removeCapabilityMutation = useMutation({
    mutationFn: async (capability: string) => {
      return hospitalCapabilitiesApi.removeCapability(hospitalId, capability)
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['hospital-capabilities', hospitalId] })
      message.success('Capability removed successfully')
    },
    onError: (error: unknown) => {
      message.error(getApiErrorMessage(error, 'Failed to remove capability'))
    },
  })

  const handleAdd = () => {
    if (!newCapability.trim()) {
      message.warning('Please enter a capability')
      return
    }
    addCapabilityMutation.mutate(newCapability.trim())
  }

  const handleRemove = (capability: string) => {
    removeCapabilityMutation.mutate(capability)
  }

  const capabilities = data || []

  return (
    <Card
      title="Hospital Capabilities"
      extra={
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => setIsAdding(!isAdding)}
          size="small"
        >
          {isAdding ? 'Cancel' : 'Add'}
        </Button>
      }
      loading={isLoading}
    >
      {isAdding && (
        <div style={{ marginBottom: 16 }}>
          <Space.Compact style={{ width: '100%' }}>
            <Input
              placeholder="Enter capability code or ID"
              value={newCapability}
              onChange={(e) => setNewCapability(e.target.value)}
              onPressEnter={handleAdd}
              autoFocus
            />
            <Button type="primary" onClick={handleAdd} loading={addCapabilityMutation.isPending}>
              Add
            </Button>
          </Space.Compact>
          <div style={{ marginTop: 8, fontSize: 12, color: '#666' }}>
            Enter a capability code (e.g., "mh", "psy") or UUID
          </div>
        </div>
      )}

      {capabilities.length === 0 ? (
        <Empty description="No capabilities configured for this hospital" />
      ) : (
        <Space wrap size={[8, 8]}>
          {capabilities.map((capability) => (
            <Tag
              key={capability}
              color="blue"
              closable
              onClose={(e) => {
                e.preventDefault()
                // Show popconfirm instead of immediate delete
              }}
              closeIcon={
                <Popconfirm
                  title="Remove Capability"
                  description={`Remove "${capability}"?`}
                  onConfirm={() => handleRemove(capability)}
                  okText="Yes"
                  cancelText="No"
                >
                  <DeleteOutlined style={{ fontSize: 12 }} onClick={(e) => e.stopPropagation()} />
                </Popconfirm>
              }
              style={{ fontSize: 13, padding: '4px 8px', cursor: 'default' }}
            >
              {capability}
            </Tag>
          ))}
        </Space>
      )}

      <div style={{ marginTop: 16, fontSize: 12, color: '#666' }}>
        Total capabilities: {capabilities.length}
      </div>
    </Card>
  )
}
